import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import DeletionModal from "../components/modals/DeletionModal";
import BaseRecordRenderer from "@reusables/BaseRecordRenderer";
import { useGetSupplierQuery } from "@redux/features/suppliers/suppliersApi";
import { ability, Can } from "@/casl.config";
import { BaseTinyActionIcon, TinyActionIconType } from "@reusables/BaseTinyActionIcon";
import GeneralTabPreview from "../components/tabs/previews/GeneralTab";
import AddressTabPreview from "../components/tabs/previews/AddressTab";
import SettingsTab from "../components/tabs/previews/SettingsTab";
import PurchaseOrderTab from "../components/tabs/previews/PurchaseOrderTab";
import ReceivesTab from "../components/tabs/previews/ReceivesTab";
import BaseTooltip from "@reusables/BaseTooltip";
import BasePermissionBlocker from "@reusables/BasePermissionBlocker";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import BaseTabs from "@reusables/BaseTabs";
import BaseSyncedFromSourcesBadge from "@reusables/BaseSyncedFromSourcesBadge";

export default function SupplierDetailsPage() {
    const { t } = useTranslation("", { keyPrefix: "suppliers" });
    const history = useHistory();
    const { id } = useParams<{ id: string }>();

    const { data: supplier, isLoading: supplierLoading } = useGetSupplierQuery(parseInt(id));

    const [isDeletionModalOpen, setIsDeletionModalOpen] = useState<boolean>(false);

    return (
        <>
            <Can not I="view" a="supplier">
                <div className="levitation-extended">
                    <NoPermissionBanner />
                </div>
            </Can>

            <Can I="view" a="supplier">
                <BaseRecordRenderer
                    record={supplier}
                    loading={supplierLoading}
                >
                    <div className="flex items-center mb-[40px]">
                        <div className={"flex flex-row items-center grow space-x-3"}>
                            <div className="text-accent text-2xl font-bold">{supplier?.name}</div>
                            <BaseSyncedFromSourcesBadge
                                integrations={[
                                    {
                                        slug: "tripletex",
                                        integrated: () => !!supplier?.tripletex_id
                                    },
                                    {
                                        slug: "twenty_four_seven_office",
                                        integrated: () => !!supplier?.twenty_four_seven_office_id
                                    },
                                    {
                                        slug: "poweroffice",
                                        integrated: () => !!supplier?.poweroffice_id
                                    }
                                ]}
                                width={24}
                                height={24}
                            />
                        </div>
                        <div className="space-x-[8px] flex items-center h-full">
                            <BasePermissionBlocker action="create" module="purchase_order" placement="bottom-start">
                                <BaseTooltip active={ability.can("create", "purchase_order")}
                                             title={`${t("viewPage.sideButtons.createPurchase")}`}
                                             placement="bottom-start">
                                    <BaseTinyActionIcon
                                        type={TinyActionIconType.PurchaseOrder}
                                        onClick={() => ability.can("create", "purchase_order") && void history.push(`/dashboard/purchasing/new`, { supplier: supplier })}
                                    />
                                </BaseTooltip>
                            </BasePermissionBlocker>
                            {/* <BaseTooltip title={`${t("sideButtons.startReceiving")}`} placement="bottom-start">
                                <BaseTinyActionIcon
                                    type={TinyActionIconType.StartReceiving}
                                    onClick={() => void history.push(`/dashboard/receive/new`)}
                                />
                            </BaseTooltip> */}
                            <BasePermissionBlocker action="edit" module="supplier" placement="bottom-start">
                                <BaseTooltip active={ability.can("edit", "supplier")}
                                             title={`${t("viewPage.sideButtons.edit")}`} placement="bottom-start">
                                    <BaseTinyActionIcon
                                        type={TinyActionIconType.Edit}
                                        onClick={() => history.push(`/dashboard/suppliers/${id}/edit`)}
                                    />
                                </BaseTooltip>
                            </BasePermissionBlocker>

                            <BasePermissionBlocker action="delete" module="supplier" placement="bottom-start">
                                <BaseTooltip active={ability.can("delete", "supplier")}
                                             title={`${t("viewPage.sideButtons.delete")}`} placement="bottom-start">
                                    <BaseTinyActionIcon
                                        type={TinyActionIconType.Delete}
                                        onClick={() => setIsDeletionModalOpen(true)}
                                    />
                                </BaseTooltip>
                            </BasePermissionBlocker>
                        </div>
                    </div>
                    <BaseTabs
                        tabs={[
                            {
                                key: "general",
                                title: t("general.tabs.general.heading"),
                                content: <GeneralTabPreview supplier={supplier as NonNullable<typeof supplier>} />
                            },
                            {
                                key: "address",
                                title: t("general.tabs.address.heading"),
                                content: <AddressTabPreview supplier={supplier as NonNullable<typeof supplier>} />
                            },
                            {
                                key: "purchase_orders",
                                title: t("general.tabs.purchaseOrders.heading"),
                                content: <PurchaseOrderTab supplier={supplier as NonNullable<typeof supplier>} />
                            },
                            {
                                key: "receives",
                                title: t("general.tabs.receives.heading"),
                                content: <ReceivesTab supplier={supplier as NonNullable<typeof supplier>} />
                            },
                            {
                                key: "settings",
                                title: t("general.tabs.settings.heading"),
                                content: <SettingsTab supplier={supplier as NonNullable<typeof supplier>} />
                            }
                        ]}
                    />
                </BaseRecordRenderer>
            </Can>

            <DeletionModal
                isOpen={isDeletionModalOpen}
                onClose={() => setIsDeletionModalOpen(false)}
                onDeletion={() => history.push("/dashboard/suppliers")}
                entity={supplier}
            />
        </>
    );
}